import { useTaskTranslation } from '../hooks/useTaskTranslation';
import { Fragment, useMemo, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TaskCard } from './TaskCard';
import styles from './KanbanView.module.css';
import columnStyles from './KanbanColumn.module.css';
import type { Task } from '../../../store/slices/tasks';
import { TASK_STATUSES, TaskStatus } from '../../../store/slices/tasks';
import { taskStatusKey, taskStatusLabel } from '../taskUtils';

type Variant = 'kanban' | 'detailed';

const SortableCard = ({
  task,
  variant,
  disabled,
  onTaskClick,
}: {
  task: Task;
  variant: Variant;
  disabled: boolean;
  onTaskClick?: (task: Task) => void;
}) => {
  const sortable = useSortable({ id: `task-${task.task_id}`, disabled });
  return (
    <div
      ref={sortable.setNodeRef}
      className={`${columnStyles.sortableCard} ${
        sortable.isDragging ? columnStyles.draggingCard : ''
      }`}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }}
    >
      <TaskCard
        task={task}
        variant={variant}
        onClick={() => {
          if (!sortable.isDragging) onTaskClick?.(task);
        }}
        dragRef={sortable.setActivatorNodeRef}
        dragProps={disabled ? undefined : { ...sortable.attributes, ...sortable.listeners }}
      />
    </div>
  );
};

const Column = ({
  status,
  tasks,
  variant,
  canUpdate,
  pendingTaskIds,
  onTaskClick,
  onCollapse,
}: {
  status: string;
  onCollapse: () => void;
  tasks: Task[];
  variant: Variant;
  canUpdate: boolean;
  pendingTaskIds: number[];
  onTaskClick?: (task: Task) => void;
}) => {
  const { translations, taskText } = useTaskTranslation();
  const validTarget = TASK_STATUSES.some((value) => taskStatusKey(value) === status);
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${status}`,
    disabled: !canUpdate || !validTarget,
  });
  return (
    <section
      className={columnStyles.column}
      aria-label={taskText('task_board.status_tasks', {
        status: taskStatusLabel(status, translations),
      })}
    >
      <div className={columnStyles.columnHeader}>
        <h3 className={columnStyles.columnTitle}>
          {taskStatusLabel(status, translations)} ({tasks.length})
        </h3>
        <button
          type="button"
          className={columnStyles.collapseButton}
          onClick={onCollapse}
          aria-label={taskText('task_board.collapse_status_column', {
            status: taskStatusLabel(status, translations),
          })}
          aria-expanded="true"
          title={translations('task_board.collapse_column')}
        >
          <span aria-hidden="true">−</span>
        </button>
      </div>
      <div
        ref={setNodeRef}
        className={`${columnStyles.columnBody} ${isOver ? columnStyles.columnOver : ''}`}
      >
        <SortableContext
          items={tasks.filter((task) => task.task_id != null).map((task) => `task-${task.task_id}`)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.map((task, index) =>
            task.task_id == null ? (
              <TaskCard
                key={`missing-${index}`}
                task={task}
                variant={variant}
                onClick={() => onTaskClick?.(task)}
              />
            ) : (
              <SortableCard
                key={task.task_id}
                task={task}
                variant={variant}
                disabled={!canUpdate || pendingTaskIds.includes(task.task_id)}
                onTaskClick={onTaskClick}
              />
            )
          )}
          {tasks.length === 0 && (
            <div className={columnStyles.emptyPlaceholder}>
              {translations('task_board.no_tasks')}
            </div>
          )}
        </SortableContext>
      </div>
    </section>
  );
};

const ResizableColumn = ({
  collapsed,
  onCollapsedChange,
  defaultSize,
  minSize,
  order,
  ...columnProps
}: Omit<Parameters<typeof Column>[0], 'onCollapse'> & {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  defaultSize: number;
  minSize: number;
  order: number;
}) => {
  const { translations, taskText } = useTaskTranslation();
  const label = taskStatusLabel(columnProps.status, translations);

  if (collapsed) {
    return (
      <section
        className={columnStyles.collapsedColumn}
        aria-label={taskText('task_board.status_tasks', { status: label })}
      >
        <button
          type="button"
          className={columnStyles.collapseButton}
          onClick={() => onCollapsedChange(false)}
          aria-label={taskText('task_board.expand_status_column', { status: label })}
          aria-expanded="false"
          title={translations('task_board.expand_column')}
        >
          <span aria-hidden="true">+</span>
        </button>
        <h3 className={columnStyles.collapsedTitle}>
          {label} ({columnProps.tasks.length})
        </h3>
      </section>
    );
  }

  return (
    <Panel
      id={`task-column-${columnProps.status}`}
      order={order}
      defaultSize={defaultSize}
      minSize={minSize}
      collapsible
      collapsedSize={0}
      onCollapse={() => onCollapsedChange(true)}
    >
      <Column {...columnProps} onCollapse={() => onCollapsedChange(true)} />
    </Panel>
  );
};

export const KanbanView = ({
  tasks,
  variant = 'kanban',
  onTaskClick,
  canUpdate,
  pendingTaskIds,
  onStatusChange,
}: {
  tasks: Task[];
  variant?: Variant;
  onTaskClick?: (task: Task) => void;
  canUpdate: boolean;
  pendingTaskIds: number[];
  onStatusChange: (taskId: number, status: TaskStatus) => Promise<void>;
}) => {
  const { translations, taskText } = useTaskTranslation();
  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(() => new Set());
  const [activeId, setActiveId] = useState<number | null>(null);
  const [order, setOrder] = useState<number[]>([]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space', 'Enter'] },
    })
  );
  const columns = useMemo(
    () =>
      Array.from(
        new Set([
          ...TASK_STATUSES.map(taskStatusKey),
          ...tasks.map((task) => taskStatusKey(task.status)),
        ])
      ),
    [tasks]
  );
  const expandedColumns = columns.filter((status) => !collapsedColumns.has(status));
  const orderedTasks = useMemo(
    () =>
      [...tasks].sort((a, b) => {
        const left = order.indexOf(a.task_id!);
        const right = order.indexOf(b.task_id!);
        return (left < 0 ? order.length : left) - (right < 0 ? order.length : right);
      }),
    [tasks, order]
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || !canUpdate) return;
    const task = tasks.find((item) => `task-${item.task_id}` === active.id);
    if (task?.task_id == null || pendingTaskIds.includes(task.task_id)) return;
    const overTask = tasks.find((item) => `task-${item.task_id}` === over.id);
    const targetKey = overTask
      ? taskStatusKey(overTask.status)
      : String(over.id).replace(/^column-/, '');
    if (taskStatusKey(task.status) === targetKey) {
      const ids = orderedTasks.filter((item) => item.task_id != null).map((item) => item.task_id!);
      const from = ids.indexOf(task.task_id);
      const to = overTask?.task_id != null ? ids.indexOf(overTask.task_id) : ids.length - 1;
      if (from >= 0 && to >= 0) setOrder(arrayMove(ids, from, to));
      return;
    }
    const status = TASK_STATUSES.find((value) => taskStatusKey(value) === targetKey);
    if (status) void onStatusChange(task.task_id, status);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={({ active }) => {
        setActiveId(tasks.find((task) => `task-${task.task_id}` === active.id)?.task_id ?? null);
      }}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <div className={styles.kanban}>
        <PanelGroup
          key={expandedColumns.join(',')}
          direction="horizontal"
          className={styles.panels}
          style={{
            height: 'calc(100vh - 220px)',
            minWidth: columns.reduce(
              (width, status) => width + (collapsedColumns.has(status) ? 48 : 240),
              (columns.length - 1) * 16
            ),
          }}
        >
          {columns.map((status, index) => (
            <Fragment key={status}>
              {!collapsedColumns.has(status) && expandedColumns.indexOf(status) > 0 && (
                <PanelResizeHandle
                  className={styles.resizeHandle}
                  aria-label={taskText('task_board.resize_first_and_second_columns', {
                    first: taskStatusLabel(
                      expandedColumns[expandedColumns.indexOf(status) - 1],
                      translations
                    ),
                    second: taskStatusLabel(status, translations),
                  })}
                />
              )}
              <ResizableColumn
                status={status}
                order={index}
                defaultSize={100 / Math.max(1, expandedColumns.length)}
                minSize={Math.min(12, 100 / columns.length)}
                collapsed={collapsedColumns.has(status)}
                onCollapsedChange={(collapsed) => {
                  setCollapsedColumns((previous) => {
                    const next = new Set(previous);
                    if (collapsed) next.add(status);
                    else next.delete(status);
                    return next;
                  });
                }}
                tasks={orderedTasks.filter((task) => taskStatusKey(task.status) === status)}
                variant={variant}
                canUpdate={canUpdate}
                pendingTaskIds={pendingTaskIds}
                onTaskClick={onTaskClick}
              />
            </Fragment>
          ))}
        </PanelGroup>
      </div>
      <DragOverlay>
        {activeId != null && tasks.find((task) => task.task_id === activeId) && (
          <TaskCard task={tasks.find((task) => task.task_id === activeId)!} variant={variant} />
        )}
      </DragOverlay>
    </DndContext>
  );
};
